import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { Eyebrow } from "@/components/Section";
import { Reveal } from "@/components/Reveal";

export const WHATSAPP_NUMBER = "23290712518";

export function whatsappLink(message) {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

export function ToolShell({ eyebrow, title, lead, children, testid }) {
  return (
    <section data-testid={testid} className="bg-mist grid-lines-light min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-32 pb-24">
        <Link to="/energy" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-700 hover:text-emerald-900">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Energy Intelligence
        </Link>
        <Reveal y={14}>
          <Eyebrow>{eyebrow}</Eyebrow>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 text-balance">
            {title}
          </h1>
          {lead && <p className="mt-4 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-600">{lead}</p>}
        </Reveal>
        <div className="mt-10">{children}</div>
      </div>
    </section>
  );
}

export const fieldCls = "h-12 w-full rounded-md border border-slate-300 bg-white px-3.5 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600";

export function NumInput({ label, value, onChange, min = 0, max = 1000000, step = "any", suffix, hint, testid }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700">{label}</span>
      <span className="relative mt-1.5 block">
        <input
          type="number" min={min} max={max} step={step} value={value} data-testid={testid}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className={fieldCls}
        />
        {suffix && <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">{suffix}</span>}
      </span>
      {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

export function TextInput({ label, value, onChange, hint, testid, type = "text", required }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700">{label}{required ? " *" : ""}</span>
      <input type={type} value={value} data-testid={testid} required={required}
        onChange={(e) => onChange(e.target.value)} className={`${fieldCls} mt-1.5`} />
      {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

export function SelectInput({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700">{label}</span>
      <select value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className={`${fieldCls} mt-1.5`}>
        {options.map((o) => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    </label>
  );
}

export function ErrorBox({ msg }) {
  if (!msg) return null;
  return (
    <p role="alert" data-testid="tool-error" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{msg}</p>
  );
}

export function ResultGrid({ items }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="result-grid">
      {items.map((r) => (
        <div key={r.label} className="rounded-lg border border-slate-200 bg-white p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500">{r.label}</p>
          <p className="mt-2 font-display text-xl sm:text-2xl font-extrabold text-slate-900">{r.value}</p>
          {r.hint && <p className="mt-1 text-[11px] leading-relaxed text-slate-500">{r.hint}</p>}
        </div>
      ))}
    </div>
  );
}

export function EstimateNote() {
  return (
    <p className="rounded-lg border border-slate-200 bg-white p-4 text-xs leading-relaxed text-slate-500">
      All figures are estimates from a preliminary engineering assessment. Actual results depend on system design,
      usage, weather, equipment, fuel prices and installation conditions, and must be verified through a BNM site
      assessment before installation.
    </p>
  );
}

export function sle(n) {
  return `SLE ${Number(n).toLocaleString()}`;
}

export function WhatsAppCTA({ message, label = "Chat with BNM on WhatsApp" }) {
  return (
    <a
      href={whatsappLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-cta"
      className="inline-flex items-center gap-2 rounded-md border border-emerald-500 bg-white px-6 py-3.5 font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
    >
      <MessageCircle className="h-4 w-4" aria-hidden="true" />
      {label}
    </a>
  );
}

export function ToolCard({ to, title, desc, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group rounded-lg border border-slate-200 bg-white p-6 h-full block transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-emerald-300 hover:shadow-lg"
    >
      <h3 className="font-display text-base sm:text-lg font-bold tracking-tight text-slate-900 group-hover:text-emerald-800 transition-colors">
        {title}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{desc}</p>
      <span className="mt-4 inline-block font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-600">Open tool →</span>
    </Link>
  );
}
